import type { PropsWithChildren, ReactNode } from "react";
import { Alert, Button, Card, Container, Spinner, Table } from "react-bootstrap";

export function PageFrame({ children, narrow = false }: PropsWithChildren<{ narrow?: boolean }>) {
  return (
    <Container as="main" className={narrow ? "page-frame page-frame--narrow" : "page-frame"}>
      {children}
    </Container>
  );
}

export function LoadingState({ label = "Loading" }: { label?: string }) {
  return (
    <div className="state-panel" role="status">
      <Spinner animation="border" size="sm" /> <span>{label}…</span>
    </div>
  );
}

export function EmptyState({ title, children }: PropsWithChildren<{ title: string }>) {
  return (
    <Card className="state-panel">
      <Card.Body>
        <Card.Title>{title}</Card.Title>
        {children}
      </Card.Body>
    </Card>
  );
}

export function ErrorState({
  title = "Something went wrong",
  children,
  retry
}: PropsWithChildren<{ title?: string; retry?: () => void }>) {
  return (
    <Alert variant="danger">
      <Alert.Heading>{title}</Alert.Heading>
      {children}
      {retry ? (
        <div className="mt-3">
          <Button variant="outline-danger" onClick={retry}>
            Try again
          </Button>
        </div>
      ) : null}
    </Alert>
  );
}

export function ResponsiveTable({
  caption,
  headers,
  rows
}: {
  caption: string;
  headers: string[];
  rows: ReactNode[][];
}) {
  return (
    <div className="table-responsive" role="region" aria-label={caption} tabIndex={0}>
      <Table hover className="align-middle">
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} scope="col">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export function ProgressiveSection({
  title,
  summary,
  children,
  initiallyOpen = false
}: PropsWithChildren<{
  title: string;
  summary?: string;
  initiallyOpen?: boolean;
}>) {
  return (
    <details className="progressive-section" open={initiallyOpen}>
      <summary>
        <span>{title}</span>
        {summary ? <small>{summary}</small> : null}
      </summary>
      <div className="progressive-section__body">{children}</div>
    </details>
  );
}

export function WorkflowStatus({
  label,
  tone = "neutral"
}: {
  label: string;
  tone?: "neutral" | "active" | "warning" | "success";
}) {
  return <span className={`workflow-status workflow-status--${tone}`}>{label}</span>;
}

export function MutationNotice({
  state,
  message
}: {
  state: "idle" | "working" | "success" | "error";
  message?: string;
}) {
  if (state === "idle") return null;
  return (
    <Alert variant={state === "error" ? "danger" : state === "success" ? "success" : "info"}>
      {state === "working" ? <Spinner animation="border" size="sm" className="me-2" /> : null}
      {message ?? (state === "working" ? "Saving…" : "Saved.")}
    </Alert>
  );
}
